import React, {Component} from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';

import {
  fetchBudgetCycles,
  fetchBudgetPeriods,
  fetchCostCenters,
  fetchRequisitionStates,
  fetchRequisitions,
} from './forms.apis';
import {
  ILookupItem,
  IRequisitionListFilters,
  IRequisitionListItem,
} from './forms.types';

import {
  AI_ASSIST_OPTIONS,
  DEFAULT_REQUISITION_LIST_FILTERS,
} from './forms.constants';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  lookupsLoading: boolean;
  error: string;
  rows: IRequisitionListItem[];
  total: number;
  filters: IRequisitionListFilters;

  periods: ILookupItem[];
  cycles: ILookupItem[];
  costcenters: ILookupItem[];
  states: ILookupItem[];
};

export default class RequisitionListPage extends Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      lookupsLoading: false,
      error: '',
      rows: [],
      total: 0,
      filters: { ...DEFAULT_REQUISITION_LIST_FILTERS },

      periods: [],
      cycles: [],
      costcenters: [],
      states: [],
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      lookupsLoading: true,
      error: '',
    });

    try {
      const [periods, cycles, costcenters, states] = await Promise.all([
        fetchBudgetPeriods(),
        fetchBudgetCycles(),
        fetchCostCenters(),
        fetchRequisitionStates(),
      ]);

      this.setState(
        {
          periods,
          cycles,
          costcenters,
          states,
          lookupsLoading: false,
        },
        () => {
          this.loadRows();
        }
      );
    } catch (error: any) {
      this.setState({
        lookupsLoading: false,
        error: error?.message || 'Failed to load requisition lookups',
      });
    }
  };

  private loadRows = async (): Promise<void> => {
    const { filters } = this.state;

    this.setState({
      loading: true,
      error: '',
    });

    try {
      const result = await fetchRequisitions(filters);

      this.setState({
        rows: result.items || [],
        total: result.total || 0,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load requisitions',
      });
    }
  };

  private handleTextChange =
    (field: keyof IRequisitionListFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IRequisitionListFilters) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSearch = (): void => {
    this.loadRows();
  };

  private handleReset = (): void => {
    this.setState(
      {
        filters: { ...DEFAULT_REQUISITION_LIST_FILTERS },
      },
      () => this.loadRows()
    );
  };

  private handlePaginationChange = (model: GridPaginationModel): void => {
    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          page: model.page + 1,
          pagesize: model.pageSize,
        },
      }),
      () => this.loadRows()
    );
  };

  private handleNewRequisition = (): void => {
    if (this.props.navigate) {
      //this.props.navigate('/forms/requisitions/new');
      this.props.navigate('add');
      return;
    }

    //window.location.href = '/forms/requisitions/new';
  };

  private handleAiRequisition = (): void => {
    if (this.props.navigate) {
      //this.props.navigate('/forms/requisitions/ai');
      this.props.navigate('generate');
      return;
    }

    //window.location.href = '/forms/requisitions/ai';
  };

  private handleOpen = (row: IRequisitionListItem): void => {
    const path = `/forms/requisitions/${row.recix}`;

    if (this.props.navigate) {
      //this.props.navigate(path);
      this.props.navigate('detail',row);
      return;
    }

    //window.location.href = path;
  };

  private handleEdit = (row: IRequisitionListItem): void => {
    const path = `/forms/requisitions/${row.recix}/edit`;

    if (this.props.navigate) {
      //this.props.navigate(path);
      this.props.navigate('edit',row);
      return;
    }

    //window.location.href = path;
  };

  private renderStateChip = (value?: string | null): React.ReactNode => {
    const label = value || 'Unknown';
    return <Chip size="small" label={label} />;
  };

  private getColumns = (): GridColDef<IRequisitionListItem>[] => {
    return [
      {
        field: 'reference',
        headerName: 'Reference',
        minWidth: 150,
        flex: 0.9,
      },
      {
        field: 'recname',
        headerName: 'Title',
        minWidth: 240,
        flex: 1.6,
      },
      {
        field: 'costcenter',
        headerName: 'Cost Center',
        minWidth: 160,
        flex: 1,
        valueGetter: (_value, row) => row.costcenter?.recname || '',
      },
      {
        field: 'period',
        headerName: 'Budget Period',
        minWidth: 140,
        flex: 0.8,
        valueGetter: (_value, row) => row.period?.recname || '',
      },
      {
        field: 'requesteddate',
        headerName: 'Request Date',
        minWidth: 130,
        flex: 0.8,
        valueGetter: (_value, row) =>
          row.requesteddate ? moment(row.requesteddate).format('YYYY-MM-DD') : '',
      },
      {
        field: 'totalestimate',
        headerName: 'Total Estimate',
        minWidth: 140,
        flex: 0.9,
        type: 'number',
        valueFormatter: (value: any) => {
          const numeric = Number(value || 0);
          return numeric.toLocaleString();
        },
      },
      {
        field: 'aiassisted',
        headerName: 'AI',
        minWidth: 100,
        flex: 0.5,
        renderCell: (params: GridRenderCellParams<IRequisitionListItem, number>) =>
          params.row.aiassisted === 1 ? (
            <Chip size="small" color="primary" label="AI" />
          ) : (
            <Chip size="small" variant="outlined" label="Manual" />
          ),
      },
      {
        field: 'state',
        headerName: 'State',
        minWidth: 130,
        flex: 0.8,
        renderCell: (params: GridRenderCellParams<IRequisitionListItem>) =>
          this.renderStateChip(params.row.state?.recname),
      },
      {
        field: 'itemcount',
        headerName: 'Items',
        minWidth: 90,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<IRequisitionListItem>) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="text"
              startIcon={<VisibilityIcon />}
              onClick={() => this.handleOpen(params.row)}
            >
              Open
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<EditIcon />}
              onClick={() => this.handleEdit(params.row)}
            >
              Edit
            </Button>
          </Stack>
        ),
      },
    ];
  };

  render(): React.ReactNode {
    const {
      loading,
      lookupsLoading,
      error,
      rows,
      total,
      filters,
      periods,
      cycles,
      costcenters,
      states,
    } = this.state;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Requisitions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View, filter, and manage requisition drafts and submissions.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleNewRequisition}
            >
              New Requisition
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={this.handleAiRequisition}
            >
              AI Requisition
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={this.handleTextChange('search')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="period-filter-label">Budget Period</InputLabel>
                  <Select
                    labelId="period-filter-label"
                    value={filters.periodix}
                    label="Budget Period"
                    onChange={this.handleSelectChange('periodix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {periods.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="cycle-filter-label">Budget Cycle</InputLabel>
                  <Select
                    labelId="cycle-filter-label"
                    value={filters.cycleix}
                    label="Budget Cycle"
                    onChange={this.handleSelectChange('cycleix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {cycles.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="costcenter-filter-label">Cost Center</InputLabel>
                  <Select
                    labelId="costcenter-filter-label"
                    value={filters.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel id="state-filter-label">State</InputLabel>
                  <Select
                    labelId="state-filter-label"
                    value={filters.stateix}
                    label="State"
                    onChange={this.handleSelectChange('stateix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {states.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel id="ai-filter-label">Capture Mode</InputLabel>
                  <Select
                    labelId="ai-filter-label"
                    value={filters.aiassisted}
                    label="Capture Mode"
                    onChange={this.handleSelectChange('aiassisted')}
                  >
                    {AI_ASSIST_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.fromdate}
                  onChange={this.handleTextChange('fromdate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.todate}
                  onChange={this.handleTextChange('todate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Stack direction="row" spacing={1} sx={{ pt: { xs: 0, md: 1 } }}>
                  <Button variant="contained" onClick={this.handleSearch}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ height: 640, width: '100%' }}>
              {(loading || lookupsLoading) && rows.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '100%' }}
                  spacing={2}
                >
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Loading requisitions...
                  </Typography>
                </Stack>
              ) : (
                <DataGrid
                  rows={rows}
                  columns={this.getColumns()}
                  getRowId={(row) => row.recix}
                  loading={loading}
                  rowCount={total}
                  paginationMode="server"
                  pageSizeOptions={[10, 20, 50, 100]}
                  paginationModel={{
                    page: filters.page - 1,
                    pageSize: filters.pagesize,
                  }}
                  onPaginationModelChange={this.handlePaginationChange}
                  disableRowSelectionOnClick
                  sx={{
                    border: 0,
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.default',
                    },
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
}